/**
 * Provider de Alchemy para transacciones
 * Soporta Ethereum, Polygon, Arbitrum
 */

import { Transaction, AlchemyAssetTransfer } from '@/types/transaction.types';
import { BaseTransactionProvider } from './base-provider';
import { ALCHEMY_NETWORKS } from '@/config/transaction-networks';
import { getApiBaseUrl } from '@/config/app';

export class AlchemyProvider extends BaseTransactionProvider {
  private baseUrl: string;

  constructor(network: string, apiKey: string, timeout = 5000) {
    super(network, timeout);
    
    const alchemyNetwork = ALCHEMY_NETWORKS[network];
    if (!alchemyNetwork) {
      throw new Error(`Alchemy network not supported: ${network}`);
    }
    
    this.baseUrl = `https://${alchemyNetwork}.g.alchemy.com/v2/${apiKey}`;
  }

  async getTransactions(address: string, fromBlock?: number): Promise<Transaction[]> {
    try {
      console.log(`[Alchemy-${this.network}] Fetching transactions for ${address}`);

      const [sentTransfers, receivedTransfers] = await Promise.all([
        this.getAssetTransfers(address, null, fromBlock),
        this.getAssetTransfers(null, address, fromBlock),
      ]);

      const allTransfers = [...sentTransfers, ...receivedTransfers];
      const transactions = this.convertAlchemyTransfers(allTransfers, address);

      console.log(`[Alchemy-${this.network}] Found ${transactions.length} transactions`);
      return transactions;
    } catch (error) {
      console.error(`[Alchemy-${this.network}] Error fetching transactions:`, error);
      await this.handleRateLimit(error as Error);
      return [];
    }
  }

  async getTokenTransfers(address: string, contractAddress?: string): Promise<Transaction[]> {
    try {
      console.log(`[Alchemy-${this.network}] Fetching token transfers for ${address}`);

      const [sentTransfers, receivedTransfers] = await Promise.all([
        this.getAssetTransfers(address, null, undefined, ['erc20'], contractAddress),
        this.getAssetTransfers(null, address, undefined, ['erc20'], contractAddress),
      ]);

      const allTransfers = [...sentTransfers, ...receivedTransfers];
      const transactions = this.convertAlchemyTransfers(allTransfers, address);

      console.log(`[Alchemy-${this.network}] Found ${transactions.length} token transfers`);
      return transactions;
    } catch (error) {
      console.error(`[Alchemy-${this.network}] Error fetching token transfers:`, error);
      await this.handleRateLimit(error as Error);
      return [];
    }
  }

  async getTransactionDetails(txHash: string): Promise<Transaction | null> {
    // Camino B: intentar el servidor primero (protege la API Key de Alchemy)
    const serverResult = await this.fetchStatusFromServer(txHash, this.network);
    if (serverResult !== 'server_unavailable') {
      return serverResult; // null si pending, Transaction si confirmada/fallida
    }

    // Fallback: consultar Alchemy directamente si el servidor no está disponible
    console.log(`[Alchemy-${this.network}] Servidor no disponible, usando RPC directo`);
    return this.getTransactionDetailsFromRPC(txHash);
  }

  /**
   * Consulta el estado de la transacción a través del servidor proxy.
   * Retorna null si la tx está pendiente, un Transaction mínimo si resolvió,
   * o el string 'server_unavailable' si el servidor no responde.
   */
  private async fetchStatusFromServer(
    txHash: string,
    network: string
  ): Promise<Transaction | null | 'server_unavailable'> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/transfers/status/${txHash}?network=${network}`, {
        signal: AbortSignal.timeout(10_000),
      });

      if (!response.ok) return 'server_unavailable';

      const data = await response.json() as {
        success: boolean;
        status: 'pending' | 'confirmed' | 'failed';
        blockNumber?: string;
      };

      if (!data.success) return 'server_unavailable';
      if (data.status === 'pending') return null; // Sigue pendiente

      return {
        hash: txHash,
        from: '',
        to: '',
        value: '0',
        timestamp: 0,
        blockNumber: data.blockNumber ? parseInt(data.blockNumber) : 0,
        status: data.status,
        type: 'send',
        network: this.network,
        source: 'server_sync',
      };
    } catch {
      return 'server_unavailable';
    }
  }

  /**
   * Consulta el estado directamente en Alchemy (fallback cuando el servidor no está disponible).
   * Hace dos llamadas en paralelo: receipt para el status real y tx para los detalles.
   */
  private async getTransactionDetailsFromRPC(txHash: string): Promise<Transaction | null> {
    try {
      const [receiptResponse, txResponse] = await Promise.all([
        this.fetchWithTimeout(this.baseUrl, {
          method: 'POST',
          body: JSON.stringify({
            jsonrpc: '2.0', id: 1,
            method: 'eth_getTransactionReceipt',
            params: [txHash],
          }),
        }),
        this.fetchWithTimeout(this.baseUrl, {
          method: 'POST',
          body: JSON.stringify({
            jsonrpc: '2.0', id: 2,
            method: 'eth_getTransactionByHash',
            params: [txHash],
          }),
        }),
      ]);

      const receiptData = await receiptResponse.json();
      const txData = await txResponse.json();

      if (txData.error) throw new Error(txData.error.message);

      const receipt = receiptData.result;
      const tx = txData.result;
      if (!tx) return null;

      // receipt null = pendiente | 0x1 = confirmada | 0x0 = fallida
      let status: 'pending' | 'confirmed' | 'failed';
      if (!receipt) {
        status = 'pending';
      } else {
        status = receipt.status === '0x1' ? 'confirmed' : 'failed';
      }

      // Las txs pendientes no tienen blockNumber aún
      if (status === 'pending') return null;

      const blockNumber = receipt?.blockNumber ? parseInt(receipt.blockNumber, 16) : 0;

      return {
        hash: tx.hash,
        from: tx.from,
        to: tx.to || '',
        value: this.parseValue(tx.value ?? '0x0'),
        timestamp: 0,
        blockNumber,
        status,
        type: 'send',
        network: this.network,
        gasUsed: receipt?.gasUsed ? parseInt(receipt.gasUsed, 16).toString() : undefined,
        gasPrice: tx.gasPrice ? parseInt(tx.gasPrice, 16).toString() : undefined,
        source: 'alchemy_sync',
      };
    } catch (error) {
      console.error(`[Alchemy-${this.network}] Error fetching transaction details:`, error);
      return null;
    }
  }

  private async getAssetTransfers(
    fromAddress: string | null,
    toAddress: string | null,
    fromBlock?: number,
    category: string[] = ['external', 'erc20'],
    contractAddress?: string
  ): Promise<AlchemyAssetTransfer[]> {
    const params: any = {
      category,
      withMetadata: true,
      excludeZeroValue: true,
      maxCount: '0x3e8', // 1000 transacciones máximo
    };

    if (fromAddress) params.fromAddress = fromAddress;
    if (toAddress) params.toAddress = toAddress;
    if (fromBlock) params.fromBlock = `0x${fromBlock.toString(16)}`;
    if (contractAddress) params.contractAddresses = [contractAddress];

    const response = await this.fetchWithTimeout(this.baseUrl, {
      method: 'POST',
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'alchemy_getAssetTransfers',
        params: [params],
      }),
    });

    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error.message);
    }

    return data.result?.transfers || [];
  }

  private convertAlchemyTransfers(transfers: AlchemyAssetTransfer[], userAddress: string): Transaction[] {
    return transfers.map(transfer => {
      const isToken = transfer.category === 'erc20';

      let value: string;
      if (isToken && transfer.rawContract?.value && transfer.rawContract?.decimal) {
        // rawContract.value is hex wei; rawContract.decimal is hex decimals
        const decimals = parseInt(transfer.rawContract.decimal, 16);
        value = this.parseValue(transfer.rawContract.value, decimals);
      } else if (transfer.value != null && transfer.value !== 0) {
        // Alchemy already returned a human-readable number
        value = transfer.value.toString();
      } else {
        value = '0';
      }

      return {
        hash: transfer.hash,
        from: transfer.from,
        to: transfer.to,
        value,
        timestamp: transfer.metadata?.blockTimestamp 
          ? new Date(transfer.metadata.blockTimestamp).getTime()
          : Date.now(),
        blockNumber: parseInt(transfer.blockNum, 16),
        status: 'confirmed' as const,
        type: this.getTransactionType(transfer.from, transfer.to, userAddress),
        network: this.network,
        tokenAddress: isToken ? transfer.rawContract?.address : undefined,
        tokenSymbol: isToken ? transfer.asset : undefined,
        tokenDecimals: isToken && transfer.rawContract?.decimal
          ? parseInt(transfer.rawContract.decimal, 16)
          : undefined,
        source: 'alchemy_sync' as const,
      };
    });
  }
}